import { Group, UnstyledButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Text } from "#components/atoms/Typographies/Text";
import { getSearchPaginationItems } from "#modules/search/view/getSearchPaginationItems";
import { Icon } from "#shared/icons/Icon";

type SearchPaginationProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
};

const pageButtonStyle = (isCurrent: boolean): React.CSSProperties => ({
  width: 32,
  height: 32,
  borderRadius: radius.md,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: isCurrent ? colors.bg.button.primary.default : "transparent",
});

export const SearchPagination = ({ page, pageCount, onPageChange }: SearchPaginationProps) => {
  const { t } = useTranslation("components");
  const items = getSearchPaginationItems(page, pageCount);

  return (
    <Group justify="center" gap={spacing[2]} pt={spacing[14]} pb={spacing[8]}>
      <UnstyledButton
        aria-label={t("table.pagination.previousPage")}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        style={pageButtonStyle(false)}
      >
        <Icon name="chevronLeft" size={16} color={colors.icon.primaryDefault} />
      </UnstyledButton>
      <Group gap={spacing[1]}>
        {items.map((item, index) =>
          item === "ellipsis" ? (
            <Text
              key={`ellipsis-${index}`}
              size="small"
              type="medium"
              style={{ width: 32, textAlign: "center" }}
            >
              ...
            </Text>
          ) : (
            <UnstyledButton
              key={item}
              aria-label={t("table.pagination.pageOf", { page: item, pageCount })}
              aria-current={item === page ? "page" : undefined}
              onClick={() => onPageChange(item)}
              style={pageButtonStyle(item === page)}
            >
              <Text
                size="small"
                type="medium"
                color={item === page ? colors.text.primaryInverse : colors.text.primaryDefault}
              >
                {item}
              </Text>
            </UnstyledButton>
          ),
        )}
      </Group>
      <UnstyledButton
        aria-label={t("table.pagination.nextPage")}
        disabled={page >= pageCount}
        onClick={() => onPageChange(page + 1)}
        style={pageButtonStyle(false)}
      >
        <Icon name="chevronRight" size={16} color={colors.icon.primaryDefault} />
      </UnstyledButton>
    </Group>
  );
};
